import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useNotificationStore } from '@/stores/notificationStore';
import { blogService, generateSlug } from '@/services/blogService';
import type { BlogCategory } from '@/types/blog';

export function BlogCategories() {
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [slugManual, setSlugManual] = useState(false);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await blogService.listCategories();
      setCategories(data);
    } catch (err: any) {
      notify.error(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Auto-slug from name
  useEffect(() => {
    if (!slugManual && name) {
      setSlug(generateSlug(name));
    }
  }, [name, slugManual]);

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setSlug('');
    setDescription('');
    setMetaTitle('');
    setMetaDescription('');
    setSortOrder(0);
    setSlugManual(false);
  };

  const editCategory = (cat: BlogCategory) => {
    setEditingId(cat.id);
    setName(cat.name);
    setSlug(cat.slug);
    setDescription(cat.description || '');
    setMetaTitle(cat.meta_title || '');
    setMetaDescription(cat.meta_description || '');
    setSortOrder(cat.sort_order);
    setSlugManual(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !slug.trim()) {
      notify.error('Name and slug are required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name,
        slug,
        description: description || undefined,
        meta_title: metaTitle || undefined,
        meta_description: metaDescription || undefined,
        sort_order: sortOrder,
      };

      if (editingId) {
        await blogService.updateCategory(editingId, data);
        notify.success('Category updated');
      } else {
        await blogService.createCategory(data);
        notify.success('Category created');
      }
      resetForm();
      loadCategories();
    } catch (err: any) {
      notify.error(err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this category? Posts will be uncategorized.')) return;
    setDeleting(id);
    try {
      await blogService.deleteCategory(id);
      notify.success('Category deleted');
      if (editingId === id) resetForm();
      loadCategories();
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete category');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Blog Categories"
        description="Manage blog post categories."
      >
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/blog')}>
          Back to Posts
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Left: Category List */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No categories yet.</p>
              <p className="text-sm mt-1">Create your first category using the form.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    editingId === cat.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{cat.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {cat.post_count} post{cat.post_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      /{cat.slug}
                      {cat.description && ` — ${cat.description}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => editCategory(cat)}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(cat.id)}
                      disabled={deleting === cat.id}
                      className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Create/Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Category' : 'New Category'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Category name"
            />
            <Input
              label="Slug"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
              placeholder="category-slug"
              hint={`URL: /blog?category=${slug || '...'}`}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description (optional)"
              rows={2}
            />
            <Input
              label="Sort Order"
              type="number"
              value={String(sortOrder)}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              hint="Lower numbers appear first"
            />

            {/* SEO */}
            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-3">SEO (optional)</p>
              <div className="space-y-3">
                <Input
                  label="Meta Title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="SEO title for category page"
                />
                <Textarea
                  label="Meta Description"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="SEO description"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                isLoading={saving}
              >
                {editingId ? 'Update' : 'Create'}
              </Button>
              {editingId && (
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
